import type { HTMLAttributes } from 'react';
import { useEffect, useMemo, useState } from 'react';

import * as Sentry from '@sentry/react';
import type { PaymentElementProps } from '@stripe/react-stripe-js';
import {
  useElements,
  useStripe as useStripeHook,
} from '@stripe/react-stripe-js';
import type {
  Appearance,
  ConfirmPaymentData,
  StripeConstructorOptions,
  StripeError,
} from '@stripe/stripe-js';
import { useMutation, useQuery } from '@tanstack/react-query';

import { HTTPError } from 'src/api/createApiClient.types';

import { useLingui } from '@lingui/react/macro';
import { LogCategory } from '../logger.categories';
import { loadStripe } from '../stripeLoader';
import { appearance } from './appearance';
import { paymentElementOptions } from './paymentElementOptions';
import { isStripeUserError } from './stripeUserErrorCodes';

export class CustomStripeError extends Error {
  readonly data: StripeError;

  constructor(message: string, data: StripeError) {
    super(message, { cause: data });

    this.data = data;
  }
}

export enum StripeIntentType {
  Payment = 'payment',
  Setup = 'setup',
}

export const useStripeClient = (stripePublicKey?: string) =>
  useQuery({
    queryKey: ['stripe', stripePublicKey],
    staleTime: Infinity,
    queryFn: async () => {
      //eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const res = await loadStripe('checkout', 1, stripePublicKey!);
      if (!res) {
        throw new Error('Stripe failed to load');
      }
      return res;
    },
    retry: 2,
    enabled: !!stripePublicKey,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });

export const useStripe = (
  type: StripeIntentType,
  customAppearance?: Appearance
) => {
  const { i18n } = useLingui();
  const stripe = useStripeHook();
  const elements = useElements();
  const [isPaymentElementReady, setIsPaymentElementReady] = useState(
    !!import.meta.env.IS_TEST || false
  );

  // Apply stripe appearance
  useEffect(() => {
    if (!elements) return;
    elements.update({
      appearance: customAppearance ?? appearance,
      locale: i18n.locale as StripeConstructorOptions['locale'],
    });
  }, [elements, i18n.locale]);

  const paymentElementProps: HTMLAttributes<HTMLDivElement> &
    PaymentElementProps = useMemo(
    () => ({
      style: { display: isPaymentElementReady ? 'block' : 'none' },
      options: paymentElementOptions,
      onReady: () => {
        Sentry?.addBreadcrumb({
          category: LogCategory.Stripe,
          message: 'Payment element ready',
          data: {
            stripe,
            elements,
          },
        });
        setIsPaymentElementReady(true);
      },
      onLoadError: (error) => {
        Sentry?.captureException(error);
        Sentry?.addBreadcrumb({
          category: LogCategory.Stripe,
          message: 'Payment element failed to load',
          data: {
            error,
          },
        });
      },
    }),
    [isPaymentElementReady]
  );

  const checkout = useMutation<unknown, Error, ConfirmPaymentData>({
    mutationFn: async (confirmParams) => {
      Sentry?.addBreadcrumb({
        category: 'Stripe',
        message: 'Checkout started',
        data: {
          intent: type,
        },
      });
      if (!stripe || !elements) {
        throw new Error(
          'Stripe or StripeElements does not exists, but are required for checkout.'
        );
      }

      let response;
      if (type === StripeIntentType.Payment) {
        response = await stripe.confirmPayment({
          elements,
          confirmParams,
        });
      } else {
        response = await stripe.confirmSetup({
          elements,
          confirmParams,
        });
      }

      if (response?.error) {
        throw new CustomStripeError('Stripe error', response.error);
      }
    },
    async onError(error) {
      if (error instanceof CustomStripeError) {
        if (isStripeUserError(error.data)) {
          Sentry?.addBreadcrumb({
            category: LogCategory.Stripe,
            message: `User error: "${error.data.code ?? ''}"`,
            data: {
              declineCode: error.data.decline_code,
              message: error.data.message,
              param: error.data.param,
              docs: error.data.doc_url,
            },
          });
          return;
        }

        Sentry.withScope((scope) => {
          scope.setTags({
            'rm.is_stripe_error': true,
            'rm.stripe_error_type': error.data.type,
            'rm.stripe_error_code': error.data.code,
            'rm.stripe_decline_code': error.data.decline_code,
          });
          scope.setExtras({
            message: 'Stripe checkout error',
            stripe_error_message: error.data.message,
            stripe_error_param: error.data.param,
            stripe_error_docs: error.data.doc_url,
            stripe_error_type: error.data.type,
            stripe_error_code: error.data.code,
            stripe_decline_code: error.data.decline_code,
          });
          scope.captureException(error);
        });
      } else if (error instanceof HTTPError) {
        const responseBody = await error.response.text().catch(() => null);
        Sentry.withScope((scope) => {
          scope.setTags({
            'rm.is_stripe_error': true,
            'rm.is_http_error': true,
            'rm.http_error_url': error.request.url,
            'rm.http_error_status': error.response.statusText,
          });
          scope.setExtras({
            message: 'HTTP error during Stripe checkout',
            http_error_url: error.request.url,
            http_error_status: error.response.statusText,
            http_error_body: responseBody,
          });
          scope.captureException(error);
        });
      } else {
        Sentry.withScope((scope) => {
          scope.setTags({
            'rm.is_stripe_error': true,
          });
          scope.setExtras({
            message: 'Unknown error during Stripe checkout',
          });
          scope.captureException(error);
        });
      }
    },
  });

  return {
    stripe,
    elements,
    isPaymentElementReady,
    paymentElementProps,
    checkout,
  };
};
